import * as React from 'react'
import styled from 'styled-components'
import {
  Form,
  Input,
  Spin,
  Button,
  Dropdown,
  Menu,
  Tabs,
  Tooltip,
  Alert,
  Popover,
  Card,
  message,
  notification,
} from 'antd'
import { set, orderBy, cloneDeep, range, max, isEmpty, isEqual, remove, unset, get } from 'lodash'
import produce from 'immer'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import { HARD_SPACE } from 'helpers/utils'
import { templateLabelPlacements } from 'options/formTemplates/labelPlacements'
import FormButtons from 'elements/FormButtons'
import Select, { Option, getOptionProps } from 'elements/Select'
import Checkbox from 'elements/Checkbox'
import Icon from 'elements/Icon'
import Drawer from 'elements/Drawer'
import PageForm from 'containers/FormTemplates/FormView/PageForm'
import SectionForm from 'containers/FormTemplates/FormView/SectionForm'
import FieldForm from 'containers/FormTemplates/FormView/FieldForm'
import Modal from 'elements/Modal'
import { Row, Col } from 'elements/Grid'
import {
  getLabel,
  getSectionProps,
  getWidths,
  sortFormQuestions,
  createQuestion,
  getTotalWidth,
  allowRequired,
  extractFormQuestions,
} from 'helpers/formTemplates'
import sideDrawerItems from 'options/formTemplates/sideDrawerItems'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import ColorPicker from 'elements/ColorPicker'
import SelectFormTemplate from 'containers/FormTemplates/Select'
import Field from './Field'
import Settings from './Settings'

const Container = styled.div`
  .ant-tabs-nav-scroll {
    min-height: 46px;
  }

  .ant-tabs-tab {
    padding-right: 8px !important;

    .ant-btn-link {
      color: unset !important;
      margin-left: 8px;
      padding: 1px 0;

      .anticon {
        margin: 0 !important;
      }
    }

    &:hover,
    &.ant-tabs-tab-active {
      color: unset !important;
    }
  }
`

const Section = styled(Card)`
  .ant-card-head {
    border-bottom: none;

    .ant-card-extra {
      .ant-btn-link {
        color: unset;
      }
    }
  }

  &.ant-card-bordered + .ant-card-bordered {
    border-top: none !important;
    border-top-right-radius: 0;
    border-top-left-radius: 0;
  }

  .ant-select-search__field__placeholder {
    color: rgba(0, 0, 0, 0.33) !important;
  }

  .ant-select-selection__placeholder {
    color: rgba(0, 0, 0, 0.33) !important;
  }

  *::placeholder {
    color: rgba(0, 0, 0, 0.33) !important;
  }

  textarea {
    resize: none;
  }
`

const Actions = styled.div`
  text-align: right;
  white-space: nowrap;
  height: 16px;

  .ant-btn-link {
    color: #333;
    margin-left: 3px;
  }
`

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-height: ${(props) => (props.isDraggingOver ? '32px' : '6px')};
  outline: ${(props) => (props.isDraggingOver ? '1px dashed #d9d9d9' : 'none')};
`

const LineItem = styled.div`
  padding-left: 6px;
  padding-right: 6px;
  user-select: none;
  border: ${(props) => (props.isDragging ? '1px dashed #d9d9d9' : 'none')};
`

const SideLine = styled.div`
  display: flex;
`

const SideLineItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  width: 99px;
  height: 79px;
  margin-bottom: 6px;
  border: ${(props) => (props.isDragging ? '1px dashed #d9d9d9' : 'none')};

  &:hover {
    border: 1px dashed #d9d9d9;
  }

  & > div {
    text-align: center;
  }
`

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.containerRef = React.createRef()
  }

  componentDidMount() {
    this.fetchItem()
  }

  transformSaveItemParams = ({ item, values }) => ({
    formPages: produce(item.formPages, (draft) => {
      draft.forEach((formPage) => {
        if (formPage.id <= 0) {
          unset(formPage, 'id')
        }

        if (item.id) {
          formPage.formTemplateId = item.id
        }

        formPage.formPageSections = formPage?.formPageSections ?? []

        formPage.formPageSections.forEach((formPageSection) => {
          if (formPageSection.id <= 0) {
            unset(formPageSection, 'id')
          }

          if (formPage.id > 0) {
            formPageSection.pageId = formPage.id
          } else {
            unset(formPageSection, 'pageId')
          }

          formPageSection.formQuestions = formPageSection?.formQuestions ?? []

          formPageSection.formQuestions.forEach((formQuestion) => {
            if (formQuestion.id <= 0) {
              unset(formQuestion, 'id')
            }

            if (formPageSection.id > 0) {
              formQuestion.sectionId = formPageSection.id
            } else {
              unset(formQuestion, 'sectionId')
            }

            if (!allowRequired(formQuestion)) {
              formQuestion.required = false
            }

            formQuestion.formQuestionOptions = formQuestion?.formQuestionOptions ?? []

            formQuestion.formQuestionOptions.forEach((formQuestionOption) => {
              if (formQuestionOption.id <= 0) {
                unset(formQuestionOption, 'id')
              }

              if (formQuestion.id > 0) {
                formQuestionOption.questionId = formQuestion.id
              } else {
                unset(formQuestionOption, 'questionId')
              }
            })
          })
        })
      })
    }),
  })

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.formTemplateId,
      this.props.linkTargetRecord?.templateId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getFormGroups(),
        this.props.getUserGroups(),
        this.props.getSettings({ type: 'field' }), // HACK: Making sure this is cached
      ])

      const item = produce(responses[0].value.data, (draft) => {
        let seed = Date.now() * -1

        draft.formPages.forEach((formPage) => {
          formPage.id = formPage.id || seed--

          if (formPage.formTemplateId === 0) {
            unset(formPage, 'formTemplateId')
          }

          formPage.formPageSections.forEach((formPageSection) => {
            formPageSection.id = formPageSection.id || seed--
            formPageSection.pageId = formPage.id

            formPageSection.formQuestions.forEach((formQuestion) => {
              formQuestion.id = formQuestion.id || seed--
              formQuestion.sectionId = formPageSection.id

              formQuestion.formQuestionOptions.forEach((formQuestionOption) => {
                formQuestionOption.id = formQuestionOption.id || seed--
                formQuestionOption.questionId = formQuestion.id
              })
            })
          })
        })
      })

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        formGroups: get(responses[2], 'value.data.items', []),
        userGroups: get(responses[3], 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (name === 'active') {
          try {
            await this.props.onSave?.(true)

            const response = await this.props.updateStatus(this.state.item.id, name, value)

            showClientNotifications({ response })
          } catch (error) {
            showError({ error })

            this.props.form.setFieldsValue({ [name]: !value })
          } finally {
            await this.fetchItem()
            await this.props.onSave?.(false)
          }
        }
      }
    )

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'copyFromExistingRecord':
        this.setState({
          copyFromExistingVisible: true,
          copyFromExistingSelectedRecord: null,
        })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handlePageDrawerSaveAndClose = (saved) => {
    if (!saved.id) {
      saved.id = Date.now() * -1
    }

    this.setState(
      produce((draft) => {
        remove(draft.item.formPages, (one) => one.id === saved.id)
        draft.item.formPages.splice(saved.position - 1, 0, { ...saved })

        draft.item.formPages.forEach((formPage, index) => {
          formPage.position = index + 1
        })

        draft.pageDrawerVisible = false
      })
    )
  }

  handleSectionDrawerSaveAndClose = (saved) => {
    if (!saved.id) {
      saved.id = Date.now() * -1
    }

    this.setState(
      produce((draft) => {
        draft.item.formPages.forEach((formPage) => {
          remove(formPage.formPageSections, (one) => one.id === saved.id)
        })

        const formPage = draft.item.formPages.find((one) => one.id === saved.pageId)

        formPage.formPageSections.splice(saved.position - 1, 0, { ...saved })

        formPage.formPageSections.forEach((formPageSection, index) => {
          formPageSection.position = index + 1
        })

        draft.sectionDrawerVisible = false
      })
    )
  }

  handleFieldDrawerSaveAndClose = (saved) => {
    if (!saved.id) {
      saved.id = Date.now() * -1
    }

    this.setState(
      produce((draft) => {
        const snapshot = cloneDeep(draft.item)

        try {
          draft.item.formPages.forEach((eachPage) => {
            eachPage.formPageSections.forEach((eachSection) => {
              remove(eachSection.formQuestions, (one) => one.id === saved.id)
            })
          })

          const formPageSection = draft.item.formPages
            .flatMap((each) => each.formPageSections)
            .find((one) => one.id === saved.sectionId)

          formPageSection.formQuestions = sortFormQuestions(formPageSection.formQuestions, saved)

          draft.item.formPages.forEach((eachPage) => {
            eachPage.formPageSections.forEach((eachSection) => {
              eachSection.formQuestions = sortFormQuestions(eachSection.formQuestions)
            })
          })

          draft.fieldDrawerVisible = false
        } catch (error) {
          draft.item = snapshot

          if (error.message === 'formSectionOverflowError') {
            notification.error({
              message: t('formSectionOverflowError'),
              description: t('formSectionOverflowErrorDescription'),
            })
          } else {
            console.error(error)
          }
        }
      })
    )
  }

  deleteFormPage = (pageIndex) => {
    Modal.confirm({
      autoFocusButton: 'ok',
      title: t('confirmDeleteFormPage'),
      okType: 'danger',
      okText: t('delete'),
      cancelText: t('cancel'),
      onOk: () =>
        this.setState(
          produce((draft) => {
            draft.item.formPages.splice(pageIndex, 1)

            draft.item.formPages.forEach((formPage, index) => {
              formPage.position = index + 1
            })
          })
        ),
    })
  }

  deleteFormPageSection = (pageIndex, sectionIndex) => {
    Modal.confirm({
      autoFocusButton: 'ok',
      title: t('confirmDeleteFormPageSection'),
      okType: 'danger',
      okText: t('delete'),
      cancelText: t('cancel'),
      onOk: () =>
        this.setState(
          produce((draft) => {
            draft.item.formPages[pageIndex].formPageSections.splice(sectionIndex, 1)

            draft.item.formPages[pageIndex].formPageSections.forEach((formPageSection, index) => {
              formPageSection.position = index + 1
            })
          })
        ),
    })
  }

  deleteFormQuestion = (formQuestion) =>
    this.setState(
      produce((draft) => {
        draft.item.formPages.forEach((formPage) => {
          formPage.formPageSections.forEach((formPageSection) => {
            remove(formPageSection.formQuestions, (one) => one.id === formQuestion.id)
            formPageSection.formQuestions = sortFormQuestions(formPageSection.formQuestions)
          })
        })
      })
    )

  handlePageDrawerClose = () =>
    this.setState({
      pageDrawerVisible: false,
      pageDrawerLinkTargetRecord: null,
    })

  handleFieldDrawerClose = () =>
    this.setState({
      fieldDrawerVisible: false,
      fieldDrawerLinkTargetRecord: null,
      fieldDrawerParentRecord: null,
    })

  handleSectionDrawerClose = () =>
    this.setState({
      sectionDrawerVisible: false,
      sectionDrawerLinkTargetRecord: null,
      sectionDrawerParentRecord: null,
    })

  duplicateQuestion = (formQuestion) =>
    this.setState(
      produce((draft) => {
        const formPageSection = draft.item.formPages
          .flatMap((each) => each.formPageSections)
          .find((one) => one.id === formQuestion.sectionId)

        const cloned = cloneDeep(formQuestion)

        let seed = Date.now() * -1

        cloned.id = seed--
        cloned.line++
        cloned.position = 1
        cloned.formQuestionOptions.forEach((one) => {
          one.id = seed--
          one.questionId = cloned.id
        })

        cloned.text = `${t('field')} ${formPageSection.formQuestions.length + 1}`

        if (cloned.questionType === 'Paragraph') {
          cloned.text = `${t('paragraph')} ${
            formPageSection.formQuestions.filter((each) => each.questionType === cloned.questionType).length +
            1
          }`
        }

        if (cloned.questionType === 'Hyperlink') {
          cloned.text = `${t('hyperlink')} ${
            formPageSection.formQuestions.filter((each) => each.questionType === cloned.questionType).length +
            1
          }`
        }

        formPageSection.formQuestions
          .filter((each) => each.line >= cloned.line)
          .forEach((each) => each.line++)
        formPageSection.formQuestions.push(cloned)
        formPageSection.formQuestions = orderBy(formPageSection.formQuestions, ['line', 'position'])
      })
    )

  handleCopyFromExisting = async () => {
    try {
      this.setState({ copyFromLabelTemplateRecordLoading: true })

      const { footer, formBackgroundColor, formForegroundColor, formPages, labelPlacementType } =
        await this.props
          .getItem(this.state.copyFromExistingSelectedRecord.id)
          .then((r) => r.value.data)
          .then(cloneDeep)

      this.setState(
        produce((draft) => {
          draft.item.footer = footer
          draft.item.formBackgroundColor = formBackgroundColor
          draft.item.formForegroundColor = formForegroundColor
          draft.item.formPages = formPages
          draft.item.labelPlacementType = labelPlacementType

          let seed = Date.now() * -1

          draft.item.formPages.forEach((formPage) => {
            formPage.id = seed--

            if (draft.item.id) {
              formPage.formTemplateId = draft.item.id
            } else {
              unset(formPage, 'formTemplateId')
            }

            formPage.formPageSections.forEach((formPageSection) => {
              formPageSection.id = seed--
              formPageSection.pageId = formPage.id

              formPageSection.formQuestions.forEach((formQuestion) => {
                formQuestion.id = seed--
                formQuestion.sectionId = formPageSection.id

                formQuestion.formQuestionOptions.forEach((formQuestionOption) => {
                  formQuestionOption.id = seed--
                  formQuestionOption.questionId = formQuestion.id
                })
              })
            })
          })

          draft.copyFromExistingVisible = false
          draft.copyFromExistingSelectedRecord = null
        })
      )
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ copyFromLabelTemplateRecordLoading: false })
    }
  }

  createHandleEditPageClick = (formPage) => () =>
    this.setState({
      pageDrawerVisible: true,
      pageDrawerTitle: formPage.name,
      pageDrawerLinkTargetRecord: formPage,
    })

  createHandleAddSectionClick = (formPage) => () =>
    this.setState({
      sectionDrawerVisible: true,
      sectionDrawerTitle: t('addSection'),
      sectionDrawerLinkTargetRecord: null,
      sectionDrawerParentRecord: formPage,
    })

  createHandleEditSectionClick =
    ({ formPage, formPageSection }) =>
    () =>
      this.setState({
        sectionDrawerVisible: true,
        sectionDrawerTitle: formPageSection.name,
        sectionDrawerLinkTargetRecord: formPageSection,
        sectionDrawerParentRecord: formPage,
      })

  createHandleAddFieldClick = (formPageSection) => () =>
    this.setState({
      fieldDrawerVisible: true,
      fieldDrawerTitle: t('addField'),
      fieldDrawerParentRecord: formPageSection,
      fieldDrawerLinkTargetRecord: null,
    })

  createHandleAddPageClick = () => () =>
    this.setState({
      pageDrawerVisible: true,
      pageDrawerTitle: t('addPage'),
      pageDrawerLinkTargetRecord: null,
    })

  handleDragEnd = ({ draggableId, source, destination }) => {
    if (isEmpty(destination) || isEqual(source, destination)) {
      return
    }

    const { questionId, questionType } = JSON.parse(draggableId)
    const sourceIds = JSON.parse(source.droppableId)
    const destinationIds = JSON.parse(destination.droppableId)

    this.setState(
      produce((draft) => {
        const snapshot = cloneDeep(draft.item)

        try {
          const page = draft.item.formPages.find((one) => one.id === destinationIds.pageId)
          const section = page.formPageSections.find((one) => one.id === destinationIds.sectionId)

          if (questionId && sourceIds.pageId === destinationIds.pageId) {
            const from = page.formPageSections.find((one) => one.id === sourceIds.sectionId)
            const index = from.formQuestions.findIndex((one) => one.id === questionId)
            const [question] = from.formQuestions.splice(index, 1)

            question.line = destinationIds.line
            question.position = destination.index
            question.sectionId = section.id

            section.formQuestions = sortFormQuestions(section.formQuestions, question)
            from.formQuestions = sortFormQuestions(from.formQuestions)
          } else if (questionType) {
            const question = createQuestion({ sectionId: section.id })

            let seed = Date.now() * -1

            question.id = seed--
            question.questionType = questionType
            question.line = destinationIds.line
            question.position = destination.index
            question.text = `${t('field')} ${section.formQuestions.length + 1}`

            const formPageSection = draft.item.formPages
              .flatMap((each) => each.formPageSections)
              .find((one) => one.id === section.id)

            const [fieldWidthType] = formPageSection.formQuestions
              .filter((each) => each.line === question.line)
              .map((each) => each.fieldWidthType)
              .filter((v, i, a) => a.indexOf(v) === i) // uniq

            question.fieldWidthType =
              fieldWidthType === 'TwoThirds'
                ? 'OneThird'
                : fieldWidthType === 'ThreeQuarters'
                  ? 'OneQuarter'
                  : fieldWidthType

            question.fieldWidthType = question.fieldWidthType || 'OneThird'

            if (['Dropdown', 'SingleSelection', 'MultipleSelection'].includes(questionType)) {
              question.formQuestionOptions = range(1, 4).map((each) => {
                const name = `${t('option')} ${each}`

                return { name, value: name, position: each, id: seed-- }
              })
            }

            if (questionType === 'Paragraph') {
              question.text = `${t('paragraph')} ${
                section.formQuestions.filter((each) => each.questionType === questionType).length + 1
              }`
            }

            if (questionType === 'Hyperlink') {
              question.text = `${t('hyperlink')} ${
                section.formQuestions.filter((each) => each.questionType === questionType).length + 1
              }`
              question.questionOption = 'https://www.wikipedia.org/'
            }

            section.formQuestions = sortFormQuestions(section.formQuestions, question)
          } else {
            message.warn(t('underDevelopment'))
          }
        } catch (error) {
          draft.item = snapshot

          if (error.message === 'formSectionOverflowError') {
            notification.error({
              message: t('formSectionOverflowError'),
              description: t('formSectionOverflowErrorDescription'),
            })
          } else {
            console.error(error)
          }
        }
      })
    )
  }

  changeFieldSettings = (formQuestion, value) =>
    this.setState(
      produce((draft) => {
        const questions = extractFormQuestions(draft.item)

        const question = questions.find((one) => one.id === formQuestion.id)

        if (question) {
          const snapshot = cloneDeep(
            questions.filter((each) => each.sectionId === question.sectionId && each.line === question.line)
          )

          Object.assign(
            snapshot.find((one) => one.id === question.id),
            value
          )

          if (getTotalWidth(snapshot) <= 24) {
            Object.assign(question, value)
          } else {
            notification.error({
              message: t('formSectionOverflowError'),
              description: t('formSectionOverflowErrorDescription'),
            })
          }
        }
      })
    )

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const instanceCount = item?.instanceCount ?? 0
    const readOnly = isReadOnly(this) || instanceCount > 0
    const formTemplate = item

    return (
      <>
        <DragDropContext onDragEnd={this.handleDragEnd}>
          <Container ref={this.containerRef}>
            <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
              <Dropdown
                overlay={
                  <Menu onClick={this.handleActionsMenuClick}>
                    <Menu.Item key="copyFromExistingRecord" disabled={readOnly || Boolean(item.id)}>
                      {t('copyFromFormTemplateRecord')}
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
              >
                <Button className="form-items-actions">
                  {t('actions')}
                  <Icon type="KeyboardArrowDown" />
                </Button>
              </Dropdown>
              <div className="form-items-container">
                {instanceCount > 0 && (
                  <Alert className="mb-24" message={t('formTemplateReadOnlyInfo')} type="warning" showIcon />
                )}
                <ValidationError errors={this.state.validationErrors} />
                <Row>
                  <Col xs={24} lg={8}>
                    <Form.Item label={createLabel('name')}>
                      {createFieldDecorator('name', {
                        valuePropName: 'defaultValue',
                        trigger: 'onBlur',
                        validateTrigger: false,
                      })(<Input readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Form.Item label={createLabel('formGroupId')}>
                      {createFieldDecorator('formGroupId')(
                        <Select
                          onChange={(value) => this.setItemValue('formGroupId', value || 0)}
                          placeholder={t('none')}
                          readOnly={readOnly}
                        >
                          <Option value={0}>{t('none')}</Option>
                          {(this.state.formGroups ?? [])
                            .filter((each) => each.id)
                            .map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.displayName}</span>
                              </Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Form.Item label={createLabel('notifyGroupId')}>
                      {createFieldDecorator('notifyGroupId')(
                        <Select
                          onChange={(value) => this.setItemValue('notifyGroupId', value || 0)}
                          placeholder={t('none')}
                          readOnly={readOnly}
                        >
                          <Option value={0}>{t('none')}</Option>
                          {(this.state.userGroups ?? [])
                            .filter((each) => each.id)
                            .map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.description}</span>
                              </Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={16}>
                    <Form.Item label={createLabel('description')}>
                      {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Form.Item label={createLabel('labelPlacementType')}>
                      {createFieldDecorator('labelPlacementType')(
                        <Select
                          onChange={(value) => this.setItemValue('labelPlacementType', value)}
                          readOnly={readOnly}
                          allowClear={false}
                        >
                          {Object.entries(templateLabelPlacements).map(([key, value]) => (
                            <Option key={key} value={key}>
                              {t(value)}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={16}>
                    <Form.Item label={createLabel('footer')}>
                      {createFieldDecorator('footer')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Row className="whitespace-nowrap">
                      <Col xs={16}>
                        <Form.Item label={createLabel('formBackgroundColor')}>
                          <Row type="flex" gutter={6}>
                            <Col>
                              <ColorPicker
                                value={formTemplate.formBackgroundColor}
                                onChange={(value) => this.setItemValue('formBackgroundColor', value)}
                                readOnly={readOnly}
                              />
                            </Col>
                            <Col>
                              <ColorPicker
                                value={formTemplate.formForegroundColor}
                                onChange={(value) => this.setItemValue('formForegroundColor', value)}
                                readOnly={readOnly}
                                foreground
                              />
                            </Col>
                          </Row>
                        </Form.Item>
                      </Col>
                      <Col xs={8}>
                        <Form.Item label={HARD_SPACE}>
                          <Checkbox
                            checked={formTemplate.active}
                            onChange={(e) => this.setItemValue('active', e.target.checked)}
                            disabled={!formTemplate.id}
                            readOnly={readOnly}
                          >
                            {createLabel('active')}
                          </Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Tabs
                  tabBarExtraContent={
                    !readOnly ? (
                      <Tooltip title={t('addPage')} placement="topLeft">
                        <Button
                          size="small"
                          type="primary"
                          onClick={this.createHandleAddPageClick()}
                          disabled={readOnly}
                        >
                          <Icon type="Add" bold />
                        </Button>
                      </Tooltip>
                    ) : null
                  }
                  onChange={() => this.setState({ focusedId: null })}
                >
                  {(formTemplate?.formPages ?? []).map((formPage, pageIndex) => (
                    <Tabs.TabPane
                      key={formPage.id}
                      tab={
                        readOnly ? (
                          formPage.name
                        ) : (
                          <>
                            <span className="mr-6">{formPage.name}</span>
                            <Tooltip title={t('editPage')}>
                              <Button
                                onClick={this.createHandleEditPageClick(formPage)}
                                type="link"
                                size="small"
                              >
                                <Icon type="Edit" />
                              </Button>
                            </Tooltip>
                            <Tooltip title={t('addSection')}>
                              <Button
                                onClick={this.createHandleAddSectionClick(formPage)}
                                type="link"
                                size="small"
                              >
                                <Icon type="Add" bold />
                              </Button>
                            </Tooltip>
                            <Tooltip title={t('deletePage')}>
                              <Button onClick={() => this.deleteFormPage(pageIndex)} type="link" size="small">
                                <Icon type="Close" />
                              </Button>
                            </Tooltip>
                          </>
                        )
                      }
                      forceRender
                    >
                      {(formPage?.formPageSections ?? []).map((formPageSection, sectionIndex) => {
                        const numberOfLines = !isEmpty(formPageSection.formQuestions)
                          ? max(formPageSection.formQuestions.map((each) => each.line)) + 2
                          : 1

                        return (
                          <Section
                            key={formPageSection.id}
                            extra={
                              !readOnly ? (
                                <>
                                  <Tooltip title={t('editSection')}>
                                    <Button
                                      onClick={this.createHandleEditSectionClick({
                                        formPage,
                                        formPageSection,
                                      })}
                                      type="link"
                                      size="small"
                                    >
                                      <Icon type="Edit" color={formPageSection.headerForegroundColor} />
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title={t('addField')}>
                                    <Button
                                      onClick={this.createHandleAddFieldClick(formPageSection)}
                                      type="link"
                                      size="small"
                                    >
                                      <Icon type="Add" color={formPageSection.headerForegroundColor} bold />
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title={t('deleteSection')}>
                                    <Button
                                      onClick={() => this.deleteFormPageSection(pageIndex, sectionIndex)}
                                      type="link"
                                      size="small"
                                    >
                                      <Icon type="Close" color={formPageSection.headerForegroundColor} />
                                    </Button>
                                  </Tooltip>
                                </>
                              ) : undefined
                            }
                            {...getSectionProps(formTemplate, formPageSection)}
                          >
                            {[...Array(numberOfLines).keys()].map((line) => (
                              <Row key={line}>
                                <Droppable
                                  key={line}
                                  index={line}
                                  droppableId={JSON.stringify({
                                    pageId: formPage.id,
                                    sectionId: formPageSection.id,
                                    line,
                                  })}
                                  direction="horizontal"
                                  isDropDisabled={readOnly}
                                >
                                  {(provided, snapshot) => {
                                    const formQuestions = orderBy(formPageSection.formQuestions, [
                                      'line',
                                      'position',
                                    ]).filter((each) => each.line === line)

                                    return (
                                      <Line
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        isDraggingOver={snapshot.isDraggingOver}
                                        readOnly={readOnly}
                                      >
                                        {formQuestions.map((formQuestion) => {
                                          const { fieldWidth, labelWidth, inputWidth } = getWidths({
                                            formTemplate,
                                            formQuestion,
                                          })
                                          const color = formPageSection.overrideFormColor
                                            ? formPageSection.contentForegroundColor
                                            : formTemplate.formForegroundColor
                                          const label = getLabel({
                                            formQuestion,
                                            labelWidth,
                                            color,
                                          })
                                          const focused = this.state.focusedId === formQuestion.id
                                          const setFocusId = (id) => this.setState({ focusedId: id })
                                          const handleEdit = () =>
                                            this.setState({
                                              fieldDrawerVisible: true,
                                              fieldDrawerTitle: formQuestion.text,
                                              fieldDrawerLinkTargetRecord: formQuestion,
                                              fieldDrawerParentRecord: formPageSection,
                                            })

                                          return (
                                            <Draggable
                                              key={formQuestion.id}
                                              draggableId={JSON.stringify({
                                                questionId: formQuestion.id,
                                              })}
                                              index={formQuestion.position}
                                              isDragDisabled={readOnly}
                                            >
                                              {
                                                // eslint-disable-next-line no-shadow
                                                (provided, snapshot) => (
                                                  <LineItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    isDragging={snapshot.isDragging}
                                                    className={`ant-col ant-col-xs-24 ant-col-lg-${fieldWidth}`}
                                                  >
                                                    <Form.Item
                                                      label={label}
                                                      required={
                                                        formQuestion.required &&
                                                        allowRequired(formQuestion) &&
                                                        !isEmpty(label?.props?.children)
                                                      }
                                                      labelCol={{
                                                        xs: { span: 24 },
                                                        lg: { span: labelWidth },
                                                      }}
                                                      wrapperCol={{
                                                        xs: { span: 24 },
                                                        lg: { span: inputWidth },
                                                      }}
                                                      onClick={() => setFocusId(formQuestion.id)}
                                                      onMouseEnter={() => setFocusId(formQuestion.id)}
                                                      onMouseLeave={() => setFocusId(null)}
                                                      help={
                                                        focused && !readOnly ? (
                                                          <Actions>
                                                            <Tooltip title={t('editField')}>
                                                              <Button
                                                                type="link"
                                                                size="small"
                                                                onClick={handleEdit}
                                                              >
                                                                <Icon type="Edit" color={color} />
                                                              </Button>
                                                            </Tooltip>
                                                            <Popover
                                                              content={
                                                                <Settings
                                                                  item={formQuestion}
                                                                  rootRecord={formTemplate}
                                                                  onChange={(value) =>
                                                                    this.changeFieldSettings(
                                                                      formQuestion,
                                                                      value
                                                                    )
                                                                  }
                                                                />
                                                              }
                                                              trigger={['click']}
                                                            >
                                                              <Button type="link" size="small">
                                                                <Icon type="Settings" color={color} />
                                                              </Button>
                                                            </Popover>
                                                            <Tooltip title={t('duplicate')}>
                                                              <Button
                                                                type="link"
                                                                size="small"
                                                                onClick={() =>
                                                                  this.duplicateQuestion(formQuestion)
                                                                }
                                                              >
                                                                <Icon type="ContentCopy" color={color} />
                                                              </Button>
                                                            </Tooltip>
                                                            <Tooltip title={t('deleteField')}>
                                                              <Button
                                                                type="link"
                                                                size="small"
                                                                onClick={() =>
                                                                  this.deleteFormQuestion(formQuestion)
                                                                }
                                                              >
                                                                <Icon type="Close" color={color} />
                                                              </Button>
                                                            </Tooltip>
                                                          </Actions>
                                                        ) : undefined
                                                      }
                                                    >
                                                      <Field
                                                        question={formQuestion}
                                                        layout={labelWidth === 24 ? 'vertical' : 'horizontal'}
                                                        color={color}
                                                      />
                                                    </Form.Item>
                                                  </LineItem>
                                                )
                                              }
                                            </Draggable>
                                          )
                                        })}
                                        {provided.placeholder}
                                      </Line>
                                    )
                                  }}
                                </Droppable>
                              </Row>
                            ))}
                          </Section>
                        )
                      })}
                    </Tabs.TabPane>
                  ))}
                </Tabs>
              </div>
              <div className="form-buttons-container">
                <FormButtons
                  readOnly={readOnly}
                  onCancel={this.props.onCancel}
                  onSubmit={this.handleSubmit}
                  saveButtonLoading={this.state.saveButtonLoading}
                  saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
                />
              </div>
            </Form>
          </Container>
          {!readOnly && (
            <Drawer title={t('fields')} getContainer={this.containerRef.current} visible sideDrawer>
              <p className="mb-24">{t('fieldComponentInfo')}</p>
              {[...Array(4).keys()].map((row) => (
                <div key={row}>
                  <Droppable
                    index={row}
                    droppableId={JSON.stringify({ row })}
                    direction="horizontal"
                    isDropDisabled
                  >
                    {(provided, snapshot) => (
                      <SideLine ref={provided.innerRef} {...provided.droppableProps}>
                        {[...Array(3).keys()].map((col) => {
                          const { questionType, icon, languageKey, outlined } = sideDrawerItems[row * 3 + col]

                          return (
                            <Draggable
                              key={questionType}
                              index={col}
                              draggableId={JSON.stringify({ questionType })}
                            >
                              {
                                // eslint-disable-next-line no-shadow
                                (provided, snapshot) => (
                                  <>
                                    <SideLineItem
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      isDragging={snapshot.isDragging}
                                    >
                                      <div>
                                        <Icon type={icon} size={26} outlined={outlined} />
                                      </div>
                                      <div>{t(languageKey)}</div>
                                    </SideLineItem>
                                    {snapshot.isDragging && (
                                      <SideLineItem>
                                        <div>
                                          <Icon type={icon} size={26} outlined={outlined} />
                                        </div>
                                        <div>{t(languageKey)}</div>
                                      </SideLineItem>
                                    )}
                                  </>
                                )
                              }
                            </Draggable>
                          )
                        })}
                      </SideLine>
                    )}
                  </Droppable>
                </div>
              ))}
            </Drawer>
          )}
        </DragDropContext>
        <Drawer
          title={this.state.pageDrawerTitle}
          visible={this.state.pageDrawerVisible}
          onClose={this.handlePageDrawerClose}
          size="xs"
        >
          <PageForm
            parentRecord={formTemplate}
            linkTargetRecord={this.state.pageDrawerLinkTargetRecord}
            onSave={() => {}}
            onSaveAndClose={this.handlePageDrawerSaveAndClose}
            onCancel={this.handlePageDrawerClose}
          />
        </Drawer>
        <Drawer
          title={this.state.sectionDrawerTitle}
          visible={this.state.sectionDrawerVisible}
          onClose={this.handleSectionDrawerClose}
          size="xs"
        >
          <SectionForm
            rootRecord={formTemplate}
            parentRecord={this.state.sectionDrawerParentRecord}
            linkTargetRecord={this.state.sectionDrawerLinkTargetRecord}
            onSave={() => {}}
            onSaveAndClose={this.handleSectionDrawerSaveAndClose}
            onCancel={this.handleSectionDrawerClose}
          />
        </Drawer>
        <Drawer
          title={this.state.fieldDrawerTitle}
          visible={this.state.fieldDrawerVisible}
          onClose={this.handleFieldDrawerClose}
          size="xs"
        >
          <FieldForm
            rootRecord={formTemplate}
            parentRecord={this.state.fieldDrawerParentRecord}
            linkTargetRecord={this.state.fieldDrawerLinkTargetRecord}
            onSave={() => {}}
            onSaveAndClose={this.handleFieldDrawerSaveAndClose}
            onCancel={this.handleFieldDrawerClose}
          />
        </Drawer>
        <Modal
          title={t('selectFormTemplate')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            loading: this.state.copyFromExistingLoading,
            disabled: !this.state.copyFromExistingSelectedRecord,
          }}
          onCancel={() =>
            this.setState({
              copyFromExistingVisible: false,
              copyFromExistingSelectedRecord: null,
            })
          }
          width={992}
        >
          <SelectFormTemplate
            onSelect={(values) => this.setState({ copyFromExistingSelectedRecord: values[0] })}
            filterProps={{ allowFilterByStatus: true }}
          />
        </Modal>
      </>
    )
  }
}

export default Form.create()(FormView)
